// src/pages/RemoveBackground.jsx
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { useProductStore } from "../store/useProductStore";
import GeneratePhotoshootModal from "../components/GeneratePhotoshootModal";
import Loader from "../components/Loader";
import placeholderImage from "../assets/placeholder.jpg";
import {
  Plus,
  Download,
  Trash2,
  X,
  ExternalLink,
  AlertTriangle,
} from "lucide-react";

const FEATURE_KEY = "remove-bg";
const PAGE_SIZE = 12;

function toCloudinaryAttachmentUrl(url) {
  if (!url || typeof url !== "string") return url;
  if (!url.includes("/upload/")) return url;
  if (url.includes("/upload/fl_attachment")) return url;
  return url.replace("/upload/", "/upload/fl_attachment/");
}

export default function RemoveBackground() {
  const { getToken } = useAuth();
  const { selectedProduct } = useProductStore();
  const { projectId: paramProjectId } = useParams();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [productImages, setProductImages] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [preview, setPreview] = useState(null); // { url, id }
  const [error, setError] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);

  // ✅ infinite scroll state (match ObjectRemover.jsx pattern)
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const pageRef = useRef(1);
  const [hasMore, setHasMore] = useState(true);
  const loaderRef = useRef(null);

  // Stripe-cycle quota + payment blocking
  const [quota, setQuota] = useState({
    loading: true,
    unlimited: false,
    blocked: false,
    sub_status: "",
    is_blocked: false,
    remaining: 0,
    used: 0,
    baseCap: 0,
    imagesExtra: 0,
    period_start: null,
    period_end: null,
    window_source: null,
  });

  const previewBackdrop = useRef(null);
  const pollTimerRef = useRef(null);
  const pollInFlightRef = useRef(false);

  const projectId = selectedProduct?.id || paramProjectId;

  /* ------------------------------- helpers -------------------------------- */
  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    pollInFlightRef.current = false;
  }, []);

  // ✅ make this stable (DO NOT depend on `page`)
  const recalcHasMore = useCallback((arr, pageNum) => {
    const p = Number(pageNum) || 1;
    setHasMore(p * PAGE_SIZE < (arr?.length || 0));
  }, []);

  const fetchQuota = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/remove-background/quota`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQuota({ loading: false, ...res.data });
    } catch {
      setQuota((q) => ({ ...q, loading: false }));
    }
  }, [getToken]);

  const pickRemoveBgAssets = useCallback(
    (data) => data?.assetsByFeature?.[FEATURE_KEY] || data?.assets || [],
    []
  );

  const getMeta = useCallback((m) => {
    if (!m) return {};
    if (typeof m === "string") {
      try {
        return JSON.parse(m);
      } catch {
        return {};
      }
    }
    return m;
  }, []);

  const mapAssets = useCallback(
    (rows) =>
      (rows || [])
        .map((a) => {
          const meta = getMeta(a.metadata);
          const feature = (meta.feature || "").toLowerCase();
          const rawStatus = (meta.status || "").toLowerCase();
          const url = a.url || "";

          const isPendingByUrl =
            !url ||
            (typeof url === "string" && (url.includes("#") || url === "/spinner.gif"));
          const status = (
            rawStatus || (isPendingByUrl ? "pending" : "finished")
          ).toLowerCase();

          return {
            id: a.id,
            url: isPendingByUrl ? "/spinner.gif" : url,
            status,
            feature,
            created_at: a.created_at || a.createdAt || 0,
            _meta: meta,
          };
        })
        .filter((a) => a.feature === FEATURE_KEY)
        // hide soft-deleted + failed
        .filter((a) => {
          const st = String(a.status || "").toLowerCase();
          const meta = a._meta || {};
          const isDeleted = meta.deleted === true || st === "deleted";
          const isFailed = st === "failed";
          return !isDeleted && !isFailed;
        })
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [getMeta]
  );

  const anyPendingLocal = useMemo(() => {
    const PENDING = new Set(["pending", "queued", "processing"]);
    return items.some(
      (i) => PENDING.has(i.status) || !i.url || i.url === "/spinner.gif"
    );
  }, [items]);

  const visibleItems = useMemo(
    () => items.slice(0, page * PAGE_SIZE),
    [items, page]
  );

  const fetchProject = useCallback(async () => {
    // ✅ If no analyzed project yet, don’t error — just show CTA and empty grid.
    if (!projectId) {
      setProductImages([]);
      setItems((prev) => {
        const next = prev.filter((p) => String(p.id).startsWith("temp-")); // keep optimistic only
        recalcHasMore(next, 1);
        return next;
      });
      setError(null);
      return;
    }

    try {
      const token = await getToken();
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const imgs = Array.isArray(res.data?.images)
        ? res.data.images
        : typeof res.data?.images === "string"
        ? [res.data.images]
        : [];
      setProductImages(imgs);

      const serverList = mapAssets(pickRemoveBgAssets(res.data));

      setItems((prev) => {
        const temps = prev.filter((p) => String(p.id).startsWith("temp-"));
        const serverById = new Map(serverList.map((a) => [String(a.id), a]));
        const keptTemps = temps.filter((t) => !serverById.has(String(t.id)));
        const merged = [...keptTemps, ...serverList];
        recalcHasMore(merged, pageRef.current);
        return merged;
      });

      setError(null);
    } catch (e) {
      setError(
        e?.response?.status === 401
          ? "Session expired. Please sign in again."
          : "Failed to fetch project data"
      );
    }
  }, [getToken, projectId, mapAssets, pickRemoveBgAssets, recalcHasMore]);

  const beginPolling = useCallback(
    (prevFinishedCount) => {
      stopPolling();
      let attempts = 0;

      pollTimerRef.current = setInterval(async () => {
        if (pollInFlightRef.current) return;
        pollInFlightRef.current = true;
        attempts += 1;

        try {
          const token = await getToken();
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const serverList = mapAssets(pickRemoveBgAssets(res.data));

          setItems((prev) => {
            const temps = prev.filter((p) => String(p.id).startsWith("temp-"));
            const serverById = new Map(serverList.map((a) => [String(a.id), a]));
            const keptTemps = temps.filter((t) => !serverById.has(String(t.id)));
            const merged = [...keptTemps, ...serverList];
            recalcHasMore(merged, pageRef.current);
            return merged;
          });

          const finishedCount = serverList.filter(
            (a) => (a.status || "finished") === "finished" && !!a.url
          ).length;

          if (finishedCount > prevFinishedCount || attempts >= 40) {
            stopPolling();
            void fetchQuota();
          }
        } catch {
          if (attempts >= 40) {
            stopPolling();
            void fetchQuota();
          }
        } finally {
          pollInFlightRef.current = false;
        }
      }, 5000);
    },
    [getToken, projectId, mapAssets, pickRemoveBgAssets, stopPolling, fetchQuota, recalcHasMore]
  );

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setInitialLoading(true);

      // ✅ reset infinite scroll window on load/switch
      setPage(1);
      pageRef.current = 1;
      setHasMore(true);

      await Promise.all([fetchProject(), fetchQuota()]);
      if (!cancelled) setInitialLoading(false);
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [fetchProject, fetchQuota, projectId]);

  useEffect(() => {
    if (selectedProduct?.id && selectedProduct.id !== paramProjectId) {
      navigate(`/generate/remove-bg/${selectedProduct.id}`);
    }
  }, [selectedProduct, paramProjectId, navigate]);

  useEffect(
    () => () => {
      stopPolling();
    },
    [stopPolling]
  );

  useEffect(() => {
    if (anyPendingLocal && projectId) {
      const finished = items.filter(
        (a) => (a.status || "finished") === "finished" && !!a.url
      ).length;
      beginPolling(finished);
    }
  }, [anyPendingLocal, items, beginPolling, projectId]);

  // Keep pageRef in sync
  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  // Also keep hasMore in sync whenever page/items change
  useEffect(() => {
    setHasMore(page * PAGE_SIZE < items.length);
  }, [page, items.length]);

  /* -------------------------------- actions ------------------------------- */
  const handleGenerate = useCallback(
    async (imageUrl) => {
      setError(null);

      // ✅ No project yet → send user to Analyze (still shows CTA on the page)
      if (!projectId) {
        setError(
          "Please analyze a product first so we can save your generated assets."
        );
        navigate("/analyze"); // adjust if your route differs
        return;
      }

      // subscription gating (server enforces too)
      if (!quota.unlimited && quota.blocked) {
        setError("Your subscription is inactive. Please update payment to continue.");
        return;
      }

      // quota gating (server enforces too)
      if (!quota.unlimited && (quota.remaining || 0) <= 0) {
        navigate("/pricing");
        return;
      }

      const token = await getToken();
      const beforeFinished = items.filter(
        (i) => (i.status || "finished") === "finished" && !!i.url
      ).length;

      const clientRequestId =
        window.crypto && window.crypto.randomUUID
          ? window.crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

      const tempId = `temp-${clientRequestId}`;
      setItems((prev) => {
        const next = [{ id: tempId, url: "/spinner.gif", status: "pending" }, ...prev];
        recalcHasMore(next, pageRef.current);
        return next;
      });

      beginPolling(beforeFinished);

      try {
        const res = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/remove-background/remove-bg`,
          { imageUrl, projectId, clientRequestId },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const placeholder = res?.data?.asset;
        if (placeholder?.id) {
          setItems((prev) => {
            const next = prev.map((it) =>
              it.id === tempId ? { ...it, id: placeholder.id } : it
            );
            recalcHasMore(next, pageRef.current);
            return next;
          });
        }

        // optimistic quota decrement (pending counts as used)
        if (!quota.unlimited) {
          setQuota((q) => ({
            ...q,
            remaining: Math.max(0, (q.remaining || 0) - 1),
            used: (q.used || 0) + 1,
          }));
        }
      } catch (e) {
        setItems((prev) => {
          const next = prev.filter((x) => x.id !== tempId);
          recalcHasMore(next, pageRef.current);
          return next;
        });

        const code = e?.response?.data?.code || "";
        if (code === "subscription_blocked") {
          setError("Your subscription is inactive. Please update payment to continue.");
        } else if (code === "images_quota_exceeded") {
          setError(
            "You’ve reached your image limit for this cycle. Add an Images add-on or upgrade your plan."
          );
        } else {
          setError(e?.response?.data?.error || "Failed to remove background.");
        }

        void fetchQuota();
      }
    },
    [getToken, projectId, items, beginPolling, quota, navigate, fetchQuota, recalcHasMore]
  );

  const handleDownload = useCallback((url) => {
    const finalUrl = toCloudinaryAttachmentUrl(url);
    const a = document.createElement("a");
    a.href = finalUrl;
    a.download = "";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, []);

  const handleDelete = useCallback(
    async (id, url) => {
      // local temp card? just remove it
      if (String(id).startsWith("temp-")) {
        setItems((prev) => {
          const next = prev.filter((img) => img.id !== id);
          recalcHasMore(next, pageRef.current);
          return next;
        });
        return;
      }

      if (!window.confirm("Delete this image?")) return;
      try {
        const token = await getToken();
        await axios.delete(
          `${process.env.REACT_APP_BACKEND_URL}/api/remove-background/remove-bg`,
          {
            data: { assetId: id, imageUrl: url, projectId },
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        setItems((prev) => {
          const next = prev.filter((i) => i.id !== id && i.url !== url);
          recalcHasMore(next, pageRef.current);
          return next;
        });
        if (preview?.url === url) setPreview(null);

        // quota does NOT decrease (soft-delete); refresh is ok
        void fetchQuota();
      } catch {
        // eslint-disable-next-line no-alert
        alert("Delete failed");
      }
    },
    [getToken, preview, projectId, fetchQuota, recalcHasMore]
  );

  /* --------------------------- Infinite Scroll --------------------------- */
  const loadMore = useCallback(() => {
    if (loadingMore) return;
    setLoadingMore(true);
    setPage((p) => p + 1);
    setLoadingMore(false);
  }, [loadingMore]);

  useEffect(() => {
    if (!hasMore || initialLoading || loadingMore) return;
    const el = loaderRef.current;
    if (!el) return;

    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "100px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, initialLoading, loadingMore, loadMore]);

  /* ------------------------------- preview UX ----------------------------- */
  useEffect(() => {
    if (!preview) return;
    const onKey = (e) => e.key === "Escape" && setPreview(null);
    const onClick = (e) => {
      if (previewBackdrop.current && e.target === previewBackdrop.current) {
        setPreview(null);
      }
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [preview]);

  /* ------------------------------- quota UI -------------------------------- */
  const canGenerate = quota.unlimited || (!quota.blocked && (quota.remaining || 0) > 0);
  const totalImages = quota.unlimited
    ? Infinity
    : (quota.baseCap || 0) + (quota.imagesExtra || 0);
  const remaining = quota.unlimited ? Infinity : quota.remaining || 0;
  const used = quota.unlimited ? 0 : quota.used || 0;

  const showBar =
    !quota.loading &&
    !quota.unlimited &&
    Number.isFinite(totalImages) &&
    totalImages > 0;

  const pct = showBar
    ? Math.min(100, Math.max(0, Math.round((used / totalImages) * 100)))
    : 0;

  const isInitialLoading = initialLoading && !error && items.length === 0;

  /* ---------------------------------- UI ---------------------------------- */
  return (
    <div className="w-full px-4 sm:px-8 py-8">
      <div className="max-w-[1600px] mx-auto bg-[#15152B] border border-[#2A2A40] rounded-3xl px-8 py-10 shadow-lg">
        <div className="flex items-start justify-between gap-4 mb-2">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Remove Background</h1>
            <p className="text-white/70">Remove background from your product images using AI.</p>
          </div>

          {!quota.loading && (
            <div
              className={`flex items-center gap-3 rounded-2xl px-4 py-2 border ${
                quota.unlimited
                  ? "border-pink-600/60 bg-pink-600/10"
                  : quota.blocked
                  ? "border-red-500/40 bg-red-500/10"
                  : "border-[#2A2A40] bg-[#1C1C34]"
              }`}
              title={
                quota.unlimited
                  ? "Unlimited images this cycle"
                  : quota.blocked
                  ? "Subscription inactive"
                  : "Images usage this cycle"
              }
            >
              {quota.unlimited ? (
                <span className="text-pink-400 font-semibold text-sm sm:text-base">
                  Unlimited images
                </span>
              ) : quota.blocked ? (
                <span className="text-red-300 font-semibold text-sm sm:text-base">
                  Subscription inactive
                </span>
              ) : (
                <span className="text-white/80 text-sm sm:text-base">
                  <span className="font-semibold text-white">{remaining}</span> left of{" "}
                  <span className="font-semibold text-white">{totalImages}</span>
                </span>
              )}
            </div>
          )}
        </div>

        {showBar && (
          <div className="mb-6 mt-2">
            <div className="h-2 w-full bg-[#21213a] rounded-full overflow-hidden border border-[#2A2A40]">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${pct}%`,
                  background:
                    "linear-gradient(90deg, rgba(252,39,131,0.9) 0%, rgba(252,39,131,0.7) 100%)",
                }}
              />
            </div>
            <div className="flex justify-between mt-1 text-xs text-white/60">
              <span>Used: {used}</span>
              <span>Total: {totalImages}</span>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 flex items-center gap-2 text-red-400">
            <AlertTriangle size={18} />
            <span>{error}</span>
          </div>
        )}

        {isInitialLoading ? (
          <div className="w-full py-16 flex items-center justify-center">
            <Loader />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {/* Create Card */}
              <div
                onClick={() => {
                  // ✅ Always show CTA; if no project yet, route to Analyze instead of opening modal
                  if (!projectId) {
                    navigate("/analyze"); // adjust if your route differs
                    return;
                  }

                  if (!canGenerate) {
                    navigate("/pricing");
                    return;
                  }
                  setModalOpen(true);
                }}
                className={`flex flex-col items-center justify-center border-2 border-dashed border-[#3A3A50] text-white rounded-lg min-h-[160px] h-[210px] sm:h-[300px] ${
                  !projectId || !canGenerate
                    ? "opacity-60 cursor-not-allowed"
                    : "cursor-pointer hover:border-pink-500 hover:text-pink-500"
                } transition text-center p-6`}
                title={
                  !projectId
                    ? "Analyze a product first to start generating"
                    : !canGenerate
                    ? quota.blocked
                      ? "Subscription inactive. Click to manage plan."
                      : "Out of images. Click to manage plan."
                    : "Choose a product image to remove background"
                }
              >
                <Plus className="w-8 h-8 mb-2" />
                <p className="font-semibold text-base">Remove Image Background</p>
                <p className="text-sm text-gray-400 mt-1">
                  {!projectId
                    ? "Analyze your first product to get started"
                    : quota.unlimited
                    ? "Unlimited images"
                    : quota.blocked
                    ? "Payment required to continue"
                    : `You have ${remaining} left of ${totalImages} this cycle`}
                </p>
                {!!projectId && !canGenerate && (
                  <p className="text-xs text-red-300 mt-2">
                    {quota.blocked
                      ? "Your subscription is inactive. Update payment to continue."
                      : "You’ve reached your image limit. Upgrade or add images."}
                  </p>
                )}
              </div>

              {/* Result / Pending cards */}
              {visibleItems.map(({ id, url, status }) => {
                const PENDING = new Set(["pending", "queued", "processing"]);
                const isPending = PENDING.has(status) || !url || url === "/spinner.gif";
                const displayUrl = isPending ? "/spinner.gif" : url;

                return (
                  <div
                    key={id}
                    className="relative group bg-gradient-to-br from-[#1C1C2E] to-[#252538] border border-[#2A2A40] text-white rounded-2xl p-2 sm:p-3 transition transform hover:scale-[1.03] hover:shadow-xl hover:ring-1 hover:ring-pink-500/30 min-h-[160px] h-[210px] sm:h-[300px] flex items-center justify-center"
                    style={{ overflow: "hidden" }}
                  >
                    {isPending ? (
                      <>
                        <img
                          src="/spinner.gif"
                          alt="Generating..."
                          className="object-contain w-full mx-auto rounded-lg opacity-70 animate-pulse"
                          draggable={false}
                          style={{
                            background: "#111124",
                            height: "100%",
                            maxHeight: "calc(100% - 32px)",
                          }}
                        />
                        <div className="absolute top-2 right-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(id, url);
                            }}
                            className="bg-black/80 hover:bg-red-600 text-white rounded-full p-2 shadow-lg transition"
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <img
                          src={displayUrl}
                          alt="Removed BG"
                          className="object-contain max-h-[88%] w-full mx-auto rounded-lg transition-opacity duration-300 group-hover:opacity-70 cursor-pointer"
                          onClick={() => setPreview({ url: displayUrl, id })}
                          onError={(e) => {
                            // eslint-disable-next-line no-param-reassign
                            e.currentTarget.src = placeholderImage;
                          }}
                          draggable={false}
                          style={{
                            background: "#111124",
                            height: "100%",
                            maxHeight: "calc(100% - 32px)",
                          }}
                        />
                        <div
                          className="absolute inset-0 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition pointer-events-none z-20"
                          style={{ background: "rgba(21, 21, 43, 0.15)" }}
                        >
                          <div className="flex gap-3 pointer-events-auto">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownload(displayUrl);
                              }}
                              className="bg-black/80 hover:bg-pink-600 text-white rounded-full p-3 shadow-lg transition"
                              title="Download"
                            >
                              <Download size={20} />
                            </button>
                            <a
                              href={displayUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="bg-black/80 hover:bg-blue-600 text-white rounded-full p-3 shadow-lg transition"
                              title="Open in new tab"
                            >
                              <ExternalLink size={20} />
                            </a>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(id, displayUrl);
                              }}
                              className="bg-black/80 hover:bg-red-600 text-white rounded-full p-3 shadow-lg transition"
                              title="Delete"
                            >
                              <Trash2 size={20} />
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Infinite loader */}
            {hasMore && !initialLoading && (
              <div
                ref={loaderRef}
                className="w-full flex justify-center py-8 pointer-events-none"
              >
                {loadingMore && (
                  <img
                    src="/spinner.gif"
                    alt="Loading..."
                    className="w-7 h-7 mx-auto"
                  />
                )}
              </div>
            )}
          </>
        )}

        {modalOpen && (
          <GeneratePhotoshootModal
            productImages={productImages}
            onClose={() => setModalOpen(false)}
            onGenerate={(imageUrl) => handleGenerate(imageUrl)}
            hideSizeSelector
          />
        )}

        {/* Preview Modal */}
        {preview && (
          <div
            ref={previewBackdrop}
            className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center px-2 select-none"
          >
            <div className="relative bg-[#22223a] rounded-2xl max-w-[98vw] shadow-2xl flex flex-col">
              <button
                onClick={() => setPreview(null)}
                className="absolute top-2 right-2 bg-white/90 hover:bg-pink-600 text-black hover:text-white rounded-full p-2 shadow transition"
                title="Close"
              >
                <X size={21} />
              </button>

              <div className="p-6 flex flex-col items-center justify-center">
                <img
                  src={preview.url}
                  alt="Preview"
                  className="max-w-[95vw] max-h-[78vh] object-contain rounded-xl bg-black"
                  style={{ boxShadow: "0 4px 32px 0 rgba(30,25,60,0.5)" }}
                  draggable={false}
                />
              </div>

              <div className="flex justify-center gap-4 pb-5">
                <button
                  onClick={() => handleDownload(preview.url)}
                  className="bg-pink-600 hover:bg-pink-700 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                >
                  Download
                </button>
                <button
                  onClick={() => handleDelete(preview.id, preview.url)}
                  className="bg-red-500 hover:bg-red-600 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                >
                  Delete
                </button>
                <a
                  href={preview.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                >
                  Open in new tab
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
